import { announce } from "../lib/announce.ts";
import { cartAdd, cartChange, cartUpdate, fetchCartSection } from "../lib/cart-api.ts";
import { getButtonLabel, setButtonLabel } from "../lib/button-label.ts";
import { Component } from "../lib/component.ts";

const PREVIEW_KEY_PREFIX = "personalizationPreview:";

interface ModalPanelLike extends HTMLElement {
  open: () => void;
  refs: { content?: HTMLElement };
}

function isModalPanelLike(el: Element | null): el is ModalPanelLike {
  if (!(el instanceof HTMLElement)) return false;
  const candidate: unknown = Reflect.get(el, "open");
  return typeof candidate === "function";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

class CartDrawer extends Component {
  #panel: ModalPanelLike | null = null;

  #orphanCleanupPending = false;

  #suppressNextViewAnalytics = false;

  #previewTrigger: Element | null = null;

  #previewImage: HTMLImageElement | null = null;

  #desktopQuery = window.matchMedia("(min-width: 940px)");

  connectedCallback(): void {
    super.connectedCallback();
    const panelEl = this.querySelector("modal-panel");
    this.#panel = isModalPanelLike(panelEl) ? panelEl : null;
    document.addEventListener("submit", this.#handleFormSubmit, {
      signal: this.signal,
    });
    this.addEventListener("click", this.#handleRemoveClick, {
      signal: this.signal,
    });
    document.addEventListener("cart:render", this.#handleCartRender, {
      signal: this.signal,
    });
    this.addEventListener("mouseover", this.#handlePreviewIn, {
      signal: this.signal,
    });
    this.addEventListener("focusin", this.#handlePreviewIn, {
      signal: this.signal,
    });
    this.addEventListener("mouseout", this.#handlePreviewOut, {
      signal: this.signal,
    });
    this.addEventListener("focusout", this.#handlePreviewOut, {
      signal: this.signal,
    });

    const noteModal = document.getElementById("cart-note-modal");
    noteModal?.addEventListener("open", this.#hidePreview, {
      signal: this.signal,
    });

    document.addEventListener("cart:rendered", this.#swapPreviewImages, {
      signal: this.signal,
    });
    this.#panel?.addEventListener("open", this.#swapPreviewImages, {
      signal: this.signal,
    });
    this.#panel?.addEventListener("open", this.#handleDrawerOpen, {
      signal: this.signal,
    });

    this.#removeOrphanFeeLines();
  }

  openSuppressingAnalytics(): void {
    if (this.#panel && !this.#panel.hasAttribute("open")) {
      this.#suppressNextViewAnalytics = true;
    }
    this.#panel?.open();
  }

  #handleDrawerOpen = (): void => {
    if (this.#suppressNextViewAnalytics) {
      this.#suppressNextViewAnalytics = false;
      return;
    }
    this.#publishViewAnalytics();
  };

  #publishViewAnalytics(): void {
    const script = this.querySelector("script[data-cart-analytics]");
    if (!(script instanceof HTMLScriptElement)) return;
    try {
      const payload: unknown = JSON.parse(script.textContent ?? "");
      if (!isRecord(payload)) return;
      if (!Array.isArray(payload.items) || payload.items.length === 0) return;
      window.Shopify?.analytics?.publish?.("cart_drawer_viewed", payload);
    } catch {
      // Analytics is non-critical; swallow parse errors
    }
  }

  #handleFormSubmit = (event: SubmitEvent): void => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes("/cart/add")) return;

    event.preventDefault();

    const data = new FormData(form);
    const id = data.get("id");
    const quantity = Number(data.get("quantity")) || 1;

    if (!id) {
      form.submit();
      return;
    }

    const submitter = event.submitter;
    if (submitter instanceof HTMLButtonElement) {
      submitter.dataset.originalText = getButtonLabel(submitter);
      submitter.disabled = true;
      if (submitter.dataset.loadingText) {
        setButtonLabel(submitter, submitter.dataset.loadingText);
      }
    }

    const items = Array.from({ length: quantity }, () => ({
      id: Number(id),
      quantity: 1,
      properties: { _line_id: crypto.randomUUID() },
    }));

    cartAdd(items)
      .then(() => fetchCartSection("cart-drawer"))
      .then((sectionHtml) => {
        this.#renderSection(sectionHtml);
        this.#removeOrphanFeeLines();
        this.openSuppressingAnalytics();
        if (this.dataset.announcementAdded) announce(this.dataset.announcementAdded);
      })
      .catch(() => {
        form.submit();
      })
      .finally(() => {
        setTimeout(() => {
          if (submitter instanceof HTMLButtonElement) {
            submitter.disabled = false;
            if (submitter.dataset.loadingText) {
              setButtonLabel(submitter, submitter.dataset.originalText ?? "");
            }
            delete submitter.dataset.originalText;
          }
        }, 400);
      });
  };

  #handleRemoveClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-action="remove"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const key = button.dataset.lineItemKey;
    if (!key) return;

    const row = button.closest("[data-line-id]");
    const lineId = row?.getAttribute("data-line-id");
    const feeLineKey = row instanceof HTMLElement ? row.dataset.feeLineKey : undefined;

    const originalText = button.textContent ?? "";
    button.disabled = true;
    if (button.dataset.loadingText) {
      button.textContent = button.dataset.loadingText;
    }

    const mutation = feeLineKey
      ? cartUpdate({ [key]: 0, [feeLineKey]: 0 })
      : cartChange({ id: key, quantity: 0 });

    mutation
      .then(() => {
        if (lineId) {
          try {
            localStorage.removeItem(`${PREVIEW_KEY_PREFIX}${lineId}`);
          } catch {
            // ignore
          }
        }
        return fetchCartSection("cart-drawer");
      })
      .then((sectionHtml) => {
        this.#renderSection(sectionHtml);
        this.#removeOrphanFeeLines();
        if (this.dataset.announcementRemoved) announce(this.dataset.announcementRemoved);
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = originalText;
        if (this.dataset.announcementError) announce(this.dataset.announcementError);
      });
  };

  #handleCartRender = (event: Event): void => {
    if (!(event instanceof CustomEvent)) return;
    const detail: unknown = event.detail;
    if (!isRecord(detail)) return;
    const html = detail.html;
    if (typeof html === "string") {
      this.#renderSection(html);
      this.#removeOrphanFeeLines();
    }
    const announcement = detail.announcement;
    if (typeof announcement === "string") announce(announcement);
  };

  #renderSection(html: string): void {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const parsedTemplate = doc.querySelector("template");
    const newInner =
      parsedTemplate?.content.getElementById("cart-drawer-inner") ??
      doc.getElementById("cart-drawer-inner");
    if (!newInner) return;

    const count = newInner.dataset.itemCount ?? "0";

    // If the panel has been initialized, replace the live element
    const liveInner = this.querySelector(
      ":scope > modal-panel .modal-panel__content #cart-drawer-inner",
    );
    if (liveInner) {
      liveInner.replaceWith(newInner);
    } else {
      // Panel hasn't opened yet — replace inside the template fragment
      const template = this.querySelector("template");
      const templateInner = template?.content.getElementById("cart-drawer-inner");
      if (templateInner) {
        templateInner.replaceWith(newInner);
      }
    }

    this.#updateCounts(count);

    document.dispatchEvent(new CustomEvent("cart:rendered"));
  }

  #handlePreviewIn = (event: MouseEvent | FocusEvent): void => {
    if (!this.#desktopQuery.matches) return;
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest("[data-preview-srcset]");
    if (!trigger || trigger === this.#previewTrigger) return;
    this.#previewTrigger = trigger;
    this.#showPreview(
      trigger instanceof HTMLElement ? trigger.dataset.previewSrcset : undefined,
      trigger instanceof HTMLElement ? trigger.dataset.previewSizes : undefined,
    );
  };

  #handlePreviewOut = (event: MouseEvent | FocusEvent): void => {
    if (!this.#previewTrigger) return;
    const related =
      event.relatedTarget instanceof Element
        ? event.relatedTarget.closest("[data-preview-srcset]")
        : null;
    if (related === this.#previewTrigger) return;
    this.#previewTrigger = null;
    this.#hidePreview();
  };

  #showPreview(srcset: string | undefined, sizes: string | undefined): void {
    if (!srcset) return;
    const backdrop = this.#panel?.querySelector(".modal-panel__backdrop");
    if (!backdrop) return;

    if (!this.#previewImage) {
      this.#previewImage = document.createElement("img");
      this.#previewImage.className = "cart-drawer__preview";
      this.#previewImage.alt = "";
      backdrop.appendChild(this.#previewImage);
    }

    this.#previewImage.srcset = srcset;
    if (sizes) this.#previewImage.sizes = sizes;
    requestAnimationFrame(() => {
      this.#previewImage?.classList.add("cart-drawer__preview--visible");
    });
  }

  #hidePreview = (): void => {
    this.#previewTrigger = null;
    this.#previewImage?.classList.remove("cart-drawer__preview--visible");
  };

  #swapPreviewImages = (): void => {
    const liveInner = this.querySelector(
      ":scope > modal-panel .modal-panel__content #cart-drawer-inner",
    );
    if (!liveInner) return;

    try {
      const activeIds = new Set<string>();
      for (const item of liveInner.querySelectorAll("[data-line-id]")) {
        if (!(item instanceof HTMLElement)) continue;
        const lineId = item.dataset.lineId;
        if (!lineId) continue;
        activeIds.add(lineId);
        const dataUrl = localStorage.getItem(`${PREVIEW_KEY_PREFIX}${lineId}`);
        if (!dataUrl) continue;
        const img = item.querySelector(".cart-drawer__image");
        if (img instanceof HTMLImageElement) {
          img.src = dataUrl;
          img.srcset = "";
          img.sizes = "";
        }
      }

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (
          key?.startsWith(PREVIEW_KEY_PREFIX) &&
          !activeIds.has(key.slice(PREVIEW_KEY_PREFIX.length))
        ) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // localStorage inaccessible — skip preview swap
    }
  };

  async #removeOrphanFeeLines(): Promise<void> {
    if (this.#orphanCleanupPending) return;

    const inner =
      this.querySelector("#cart-drawer-inner") ??
      this.querySelector("template")?.content.getElementById("cart-drawer-inner");
    const orphanKeys = inner instanceof HTMLElement ? inner.dataset.orphanFeeKeys : undefined;
    if (!orphanKeys) return;

    const updates: Record<string, number> = {};
    for (const key of orphanKeys.split(",")) {
      updates[key] = 0;
    }

    this.#orphanCleanupPending = true;
    try {
      await cartUpdate(updates);
    } catch {
      // Non-critical — orphan fee lines remain until next cart action
    } finally {
      this.#orphanCleanupPending = false;
    }
  }

  #updateCounts(count: string): void {
    const num = Number(count);
    const text = num > 0 ? String(num) : "";
    const hidden = num === 0;

    const cartButton = document.querySelector("[data-cart-title]");
    const headerBadge = cartButton?.querySelector("[data-cart-count]");
    if (headerBadge) {
      headerBadge.textContent = text;
      headerBadge.toggleAttribute("hidden", hidden);
    }
    if (cartButton instanceof HTMLElement) {
      const title = cartButton.dataset.cartTitle ?? "";
      if (hidden) {
        cartButton.setAttribute("aria-label", title);
      } else {
        const label =
          num === 1
            ? (cartButton.dataset.cartCountOne ?? "")
            : (cartButton.dataset.cartCountOther ?? "").replace("2", text);
        cartButton.setAttribute("aria-label", `${title}, ${label}`);
      }
    }

    const badge =
      this.querySelector("[data-badge]") ??
      this.querySelector("template")?.content.querySelector("[data-badge]");
    if (badge) {
      badge.textContent = text;
      badge.toggleAttribute("hidden", hidden);
    }
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}
